import { useEffect, useMemo, useState } from "react";
import { t, getLang } from "@/lib/i18n";
import { Search, Info, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import PageHeader from "@/components/PageHeader";
import { formatRate, formatDate } from "@/components/home/RatesPreview";
import { Skeleton } from "@/components/ui/skeleton";

const CATS = ["All", "Paper", "Plastic", "Metal", "E-waste", "Glass", "Other"];

export default function ScrapRates() {
  const { content: c } = useSite();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/scrap-rates", { params: { lang: getLang() } }).then((r) => setData(r.data)).catch(() => setData({ rates: [] })); }, []);

  const rows = useMemo(() => (data?.rates || []).filter((r) =>
    (cat === "All" || r.category === cat) && r.name.toLowerCase().includes(q.toLowerCase())
  ), [data, cat, q]);
  const cats = CATS.filter((k) => k === "All" || (data?.rates || []).some((r) => r.category === k));

  return (
    <div data-testid="scrap-rates-page">
      <PageHeader testid="rates-header" eyebrow={`${t("Scrap rates")} · ${data?.city || c.launch_city}`} title={t("Today's indicative scrap prices.")}
        subtitle="Updated by the Kindsaver team so you have a fair idea before you sell. Prices are per kg unless mentioned.">
        <div className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--ink-soft)]" data-testid="rates-updated-at">
          <span className="w-2 h-2 rounded-full bg-[var(--leaf)]" /> {t("Last updated")} {formatDate(data?.updated_at)}
        </div>
      </PageHeader>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {cats.map((k) => (
              <button key={k} onClick={() => setCat(k)} data-testid={`rates-cat-${k.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold border whitespace-nowrap transition-colors ${cat === k ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[rgba(20,40,26,.12)] text-[var(--ink-soft)] hover:border-[var(--leaf)]"}`}>{t(k)}</button>
            ))}
          </div>
          <div className="relative md:w-72">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search an item")} data-testid="rates-search-input"
              className="w-full pl-9 pr-4 py-2.5 rounded-full border border-[rgba(20,40,26,.12)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--leaf)]/30" />
          </div>
        </div>

        <div className="mt-6 bg-white rounded-3xl border border-[rgba(20,40,26,.08)] overflow-hidden" data-testid="rates-table">
          <div className="hidden sm:grid grid-cols-12 px-6 py-3 text-xs font-bold uppercase tracking-wider text-[var(--ink-soft)] bg-[var(--paper-2)]">
            <div className="col-span-5">{t("Item")}</div><div className="col-span-2">{t("Category")}</div><div className="col-span-3">{t("Notes")}</div><div className="col-span-2 text-right">{t("Rate")}</div>
          </div>
          {!data ? (
            <div className="p-6 space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-[var(--ink-soft)]" data-testid="rates-empty">{t("No items match your search.")}</div>
          ) : rows.map((r, i) => (
            <div key={r.id} className="grid grid-cols-12 items-center px-5 sm:px-6 py-4 border-t border-[rgba(20,40,26,.06)] hover:bg-[var(--paper)] transition-colors" data-testid={`rate-row-${i}`}>
              <div className="col-span-7 sm:col-span-5 font-semibold">{t(r.name)}<div className="sm:hidden text-xs font-medium text-[var(--ink-soft)] mt-0.5">{t(r.category)}{r.note ? ` · ${r.note}` : ""}</div></div>
              <div className="hidden sm:block col-span-2 text-sm text-[var(--ink-soft)]">{t(r.category)}</div>
              <div className="hidden sm:block col-span-3 text-sm text-[var(--ink-soft)]">{r.note || "—"}</div>
              <div className="col-span-5 sm:col-span-2 text-right">
                <span className="font-display text-xl font-bold text-[var(--leaf-dark)]" data-testid={`rate-value-${i}`}>{formatRate(r)}</span>
                <span className="text-xs text-[var(--ink-soft)]"> /{t(r.unit)}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 flex gap-3 p-5 rounded-2xl bg-[var(--marigold-soft)] text-[#5C3A07]" data-testid="rates-disclaimer">
            <Info size={18} className="shrink-0 mt-0.5" /><p className="text-sm leading-relaxed">{data?.note || c.rates_note}</p>
          </div>
          <a href={c.consumer_app_url} className="btn-leaf" data-testid="rates-book-btn">{c.consumer_cta_label} <ArrowUpRight size={16} /></a>
        </div>
      </section>
    </div>
  );
}
